import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOutsideClick, useThrottle } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { Icon, SearchInput } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import countriesData from '@assets/data/countries.json';

const PositionEnum = {
  Select: 'select',
  Body: 'body',
} as const;
type PositionEnum = (typeof PositionEnum)[keyof typeof PositionEnum];
interface Value {
  countryCode: string;
  phoneNumber: string;
}
interface PhoneInputProps {
  className?: string;
  selectClassName?: string;
  iconClassName?: string;
  inputClassName?: string;
  position?: PositionEnum;
  enableSearch?: boolean;
  required?: boolean;
  value: Value;
  onChange: (value: Value) => void;
}

export const PhoneInput = (props: PhoneInputProps) => {
  const { position = PositionEnum.Body } = props;

  const [countries] = useState(countriesData);

  const [dropdown, setDropdown] = useState<boolean>(false);
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});

  const [name, setName] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const selectRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  const { t } = useTranslation();

  const updateMenuPosition = useCallback(() => {
    if (!selectRef.current || position === PositionEnum.Select) return;
    const rect = selectRef.current.getBoundingClientRect();
    setMenuStyles({
      width: rect.width,
      position: 'absolute',
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  }, [selectRef.current]);

  useEffect(() => {
    if (!dropdown || !selectRef.current) return;
    updateMenuPosition();
  }, [dropdown, selectRef.current]);

  const throttledUpdateMenuPosition = useThrottle(updateMenuPosition, 10);
  useEventListener('scroll', throttledUpdateMenuPosition, undefined, true);
  useEventListener('resize', throttledUpdateMenuPosition);
  // @ts-ignore
  useOutsideClick([selectRef, menuRef], () => setDropdown(false));

  const changeDropdownItem = (country: { code: number; name: string; shortName: string; flag: string }) => {
    setDropdown(false);
    setName(country.shortName);
    props.onChange({ ...props.value, countryCode: country.code.toString() });
  };

  const currentItem = useMemo(() => countries.find((item) => item.code.toString() === props.value.countryCode), [countries, props.value.countryCode]);
  const currentLabel = useMemo(() => {
    const label = name || currentItem?.shortName || 'CODE';
    return currentItem?.code ? `${label} +${currentItem.code}` : label;
  }, [name, currentItem?.shortName, currentItem?.code, t]);
  const filteredCountries = useMemo(() => {
    return countries.filter((country) => `${country.shortName} +${country.code}`.toLowerCase().includes(searchValue.trim().toLowerCase()));
  }, [countries, searchValue]);
  const placeholder = t('placeholders.phoneNumber');
  return (
    <div
      ref={selectRef}
      className={cn(
        'relative flex h-[37px] w-full rounded-sm border-b border-b-light bg-white transition-all duration-200 ease-in-out dark:bg-soft-black',
        PaletteUtils.isVcfcPalette &&
          'h-[50px] rounded-[100px] border border-light/70 bg-white hover:border-primary focus:border-primary dark:border-none dark:bg-white/10',
        props.className,
      )}
    >
      <div
        className={cn(
          'flex w-fit cursor-pointer select-none items-center gap-1 whitespace-nowrap py-2 pl-2 text-soft-black transition-all duration-200 ease-in-out hover:text-white-signal-black active:text-almost-black dark:text-white dark:hover:text-light-telegray dark:active:text-pearlescent-light-gray',
          PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-soft-black',
          props.selectClassName,
        )}
        onClick={() => setDropdown((prev) => !prev)}
      >
        {currentLabel}
        <Icon
          icon={<ChevronIcon />}
          className={cn('-rotate-90 transition-transform duration-200', dropdown && 'rotate-90', props.iconClassName)}
          color={PaletteUtils.getColor('primary', PaletteUtils.isVcfcPalette ? 'dark' : 'white')}
        />
      </div>
      <input
        className={cn(
          'no-arrows w-full bg-ice p-2 focus:outline-none dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'rounded-[100px] bg-white dark:bg-white/0',
          props.inputClassName,
        )}
        type='number'
        placeholder={props.required ? `${placeholder} *` : placeholder}
        value={props.value.phoneNumber}
        onChange={(e) => props.onChange({ ...props.value, phoneNumber: e.target.value })}
        onWheel={(e) => e.currentTarget.blur()}
      />
      {dropdown &&
        createPortal(
          <ul
            ref={menuRef}
            className={cn(
              'no-scrollbar absolute left-0 top-full z-[99] m-0 max-h-40 w-full overflow-auto border bg-white p-0 dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && 'rounded-2xl border-light/70',
            )}
            style={menuStyles}
          >
            {props.enableSearch && (
              <li
                className={cn(
                  'sticky left-0 top-0 border-b bg-white dark:bg-soft-black',
                  PaletteUtils.isVcfcPalette && 'border-none border-light/70',
                )}
              >
                <SearchInput inputClassName='rounded-[14px]' value={searchValue} onChange={setSearchValue} />
              </li>
            )}
            {filteredCountries.map((country, idx) => (
              <li
                key={idx}
                className={cn(
                  'flex cursor-pointer items-center justify-between px-3 py-2.5 text-sm',
                  PaletteUtils.isVcfcPalette && [
                    'px-4 text-soft-black dark:text-white/50',
                    'hover:bg-primary-lightest dark:hover:bg-slate',
                    props.value.countryCode === country.code.toString() && 'bg-primary-lightest text-primary dark:bg-slate dark:text-white',
                  ],
                  PaletteUtils.isInsigniaPalette && [
                    'text-soft-black dark:text-white',
                    'hover:bg-gray-silk dark:hover:bg-olive-gray',
                    props.value.countryCode === country.code.toString() && 'bg-gray-silk text-primary dark:bg-olive-gray dark:text-white',
                  ],
                )}
                onClick={() => changeDropdownItem(country)}
              >
                {`${country.shortName} +${country.code}`}
              </li>
            ))}
          </ul>,
          position === PositionEnum.Body ? document.body : selectRef.current!,
        )}
    </div>
  );
};
